/* eslint-disable no-unused-vars */

import _ from 'lodash';
import { useSelector } from 'react-redux';
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import moment from 'moment';
import { store } from 'redux/store';
import { useLocation, useSearchParams, useNavigate } from 'react-router-dom';

import SearchField from '@components/search-field';
import FilterHeader from '@components/filters/filter-header';
import EquipmentFilterGroups from '@components/filters/equipment-filter-group';
import SiteFilterGroups from '@components/filters/site-filter-group';
import TaskFilterGroups from '@components/filters/task-filter-group';
import EquipmentBookmark from './equipment-bookmark';

import useWindowDimensions from '@hooks/useWindowDimensions';
import { mapValues, countFilters, getEquipmentDefaultFilters } from './helper';
import { checkValueInObjectIsNotEmpty } from '@utilities/helper.util';
import { filterActions } from '@redux/slices/filters';
import { getAppDispatch } from '@utilities/dispatch.util';
import { useToastContext } from '@context/toaster.context';
import BookmarksService from '@services/bookmarks.service';
import { bookmarksActions } from '@redux/slices/bookmarks';

import style from './style.module.scss';
import ResetAndViewButton from '@components/filters/reset-view-button';
import dynamicFilterList, { getCountOnEachFilterObject } from '@utilities/dynamic-filter-list';
import DynamicFilterContainer from '@components/dynamic-filter-container';
import { hasFilterChanged } from '@utilities/filter-is-changed';
import SaveModal from '@components/modal/variants/save-modal';

const EquipmentFilter = ({
  length,
  filtersCount,
  setFilterActive,
  noGap,
  onSearch,
  resetFilters,
  setResetFilters,
  isFilterActive,
  setFiltersCount,
  onChangeFilters,
  sitesSiteTypeDataMap,
  sitesSiteStatusDataMap,
  sitesCheckPriorityDataMap,
  sitesAssignableUsersDataMap,
  sitesWorkOrderPriorityDataMap,
  sitesAreaHierarchyTreeDataMap,
  equipmentsEquipmentTypeDataMap,
  equipmentsEquipmentStatusDataMap,
  equipmentsEquipmentSubTypeDataMap,
  equipmentsEquipmentCategoryDataMap,
  equipments,
  selectedSort,
  bookmarkFilter,
  removeClassOnAssetDetailsFilters,
  psvs = false,
  pvs = false,
  detailPage = false,
  siteId = null,
  equipmentId = null,
  structureId = null,
  dynamicFilterCountOnEachField = 0
}) => {
  const { state, pathname } = useLocation();
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();
  const { width } = useWindowDimensions();
  const { ToasterUtil } = useToastContext();

  const dispatch = getAppDispatch();
  const dueDateFilter = searchParams.get('dueDate');
  const downloadFilter = searchParams.get('download');
  const keyNameFilter = searchParams.get('keyName');
  const tabFilter = searchParams.get('tab');
  const bookmarkId = searchParams.get('bookmarkId');
  const bookmarkName = searchParams.get('bookmarkName');

  const { unit, coordinates } = useSelector((state) => state.cache);
  const equipmentsFilter = useSelector((state) => state?.filters?.equipmentsFilter) || {};
  const {
    picklistsMap = {},
    areaHierarchyTreeDataMap = {},
    areaHierarchyDataMap = {}
  } = useSelector((state) => state?.picklists);

  const [isActive, setActive] = useState(isFilterActive);
  const [inputValue, setInputValue] = useState('');
  const [filters, setFilters] = useState(getEquipmentDefaultFilters);
  const [initialY, setInitialY] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [updateBookmarkModalVisible, setUpdateBookmarkModalVisible] = useState(false);

  useEffect(() => {
    setActive(isFilterActive);
  }, [isFilterActive]);

  const {
    siteTypeTags,
    siteStatusTags,
    assignableUsersTags,
    workOrderPriorityTags,
    treeData,
    equipmentCategoryTags,
    equipmentTypeTags,
    equipmentSubTypeTags,
    equipmentStatusTags,
    taskTypeTag,
    taskStatusTags,
    taskRecurringTemplateTags,
    workOrderTypeTags,
    checkRecurrancePriorityTags,
    checkTypeTags,
    checkTypeTagsMap,
    inspectionTypeTags,
    inspectionTypeTagsMap,
    inspectionRunTags,
    workOrderTypeTagMap
  } = React.useMemo(() => {
    return mapValues({
      sitesSiteTypeDataMap,
      sitesSiteStatusDataMap,
      sitesAssignableUsersDataMap,
      sitesCheckPriorityDataMap,
      sitesWorkOrderPriorityDataMap,
      sitesAreaHierarchyTreeDataMap,
      equipmentsEquipmentTypeDataMap,
      equipmentsEquipmentStatusDataMap,
      equipmentsEquipmentSubTypeDataMap,
      equipmentsEquipmentCategoryDataMap,
      equipments,
      store: { ...store.getState() }
    });
  }, [
    sitesSiteTypeDataMap,
    sitesSiteStatusDataMap,
    sitesAssignableUsersDataMap,
    sitesCheckPriorityDataMap,
    sitesWorkOrderPriorityDataMap,
    sitesAreaHierarchyTreeDataMap,
    equipmentsEquipmentTypeDataMap,
    equipmentsEquipmentStatusDataMap,
    equipmentsEquipmentSubTypeDataMap,
    equipmentsEquipmentCategoryDataMap
  ]);

  const startTouch = (e) => setInitialY(e.touches[0].clientY);

  const moveTouch = (e) => {
    let currentY = e.touches[0].clientY;
    let diffY = initialY - currentY;
    if (diffY < 0) setFilterActive(false);
    setInitialY(e.touches[0].clientY);
  };

  useEffect(() => {
    if (!_.isEmpty(bookmarkFilter)) {
      const filtersInDecode = decodeURIComponent(bookmarkFilter);
      const filterInParse = JSON.parse(filtersInDecode);
      filterInParse?.filters && setFilters(filterInParse?.filters);
      filterInParse?.filters &&
        filterInParse?.filters['searched'] &&
        setInputValue(filterInParse?.filters['searched'] || '');
    } else if (checkValueInObjectIsNotEmpty(equipmentsFilter) && !detailPage) {
      setFilters(equipmentsFilter);
      onChangeFilters?.(equipmentsFilter);
      setInputValue(equipmentsFilter?.searched || '');
      onSearch(equipmentsFilter?.searched || '');
    } else {
      setInputValue('');
      setFilters(getEquipmentDefaultFilters);
    }
    if (resetFilters) resetBtnEvent({ filterActive: width < 768 ? false : true });
  }, [resetFilters, state?.resetFilters, bookmarkFilter]);

  const isFilterChangedInBookmark = useMemo(() => {
    if (!_.isEmpty(bookmarkFilter)) {
      const filtersInDecode = decodeURIComponent(bookmarkFilter);
      const oldFilter = JSON.parse(filtersInDecode);
      const oldFilterSearch = oldFilter?.filters['searched'] || '';

      const result =
        oldFilterSearch !== inputValue
          ? true
          : hasFilterChanged({
              oldFilter: oldFilter?.filters,
              newFilter: { ...filters }
            });

      return result;
    } else return false;
  }, [bookmarkFilter, filters, inputValue]);

  useEffect(() => {
    if (downloadFilter) updateFilters('download', 'selected', downloadFilter);
  }, [downloadFilter]);

  useEffect(() => {
    if (dueDateFilter === 'today') {
      updateFilters('dueDate', 'value', moment());
      updateFilters('dueDate', 'selected', 'on');
    } else if (dueDateFilter === 'past') {
      updateFilters('dueDate', 'selected', 'past');
      updateFilters('dueDate', 'value', ['Unlimited']);
    }
  }, [dueDateFilter]);

  useEffect(() => {
    const filterCount = countFilters(filters, inputValue, { ...store.getState() }) || 0;
    !detailPage && dispatch?.(filterActions.setEquipmentFiltersCount(filterCount));
    setFiltersCount(filterCount);
  }, [filters, inputValue, detailPage]);

  useEffect(() => {
    if (isActive) document.body.classList.add('filter-open');
    else document.body.classList.remove('filter-open');
  }, [isActive]);

  useEffect(() => {
    return () => {
      if (isActive) {
        setActive(false);
        document.body.classList.remove('filter-open');
      }
    };
  }, [isActive]);

  const updateFilters = (name, key, value) => {
    const filtersCopy = { ...filters };
    let newFilter = {
      ...filtersCopy,
      [name]: {
        ...Object.assign({}, filtersCopy[name]),
        [key]: value,
        ...(key === 'selected' && value === 'on' && { value: '' }),
        ...(key === 'selected' && (value === 'past' || value === 'next') && { value: [] }),
        ...(key === 'selected' && value === 'range' && { value: ['', ''] }),
        ...(key === 'selected' && value === 'last' && { value: ['last'] })
      }
    };
    setFilters({ ...newFilter });
    onChangeFilters?.({ ...newFilter });
    !detailPage &&
      dispatch?.(filterActions.setEquipmentsFilters({ ...newFilter, searched: inputValue }));
  };

  const resetBtnEvent = ({ filterActive = true }) => {
    setResetFilters(false);
    navigate(pathname, { replace: true });
    setFilterActive(filterActive);
    setInputValue('');
    setFilters(getEquipmentDefaultFilters);
    onChangeFilters?.(getEquipmentDefaultFilters);
    onSearch('');
    !detailPage &&
      dispatch?.(
        filterActions.setEquipmentsFilters({ ...getEquipmentDefaultFilters, searched: '' })
      );
  };

  const leftBtnEvent = () => setFilterActive(false);

  const handleUserInput = (e) => {
    const valueSearched = (e && e.target.value) || '';
    onSearch?.(valueSearched);
    !detailPage &&
      dispatch?.(filterActions.setEquipmentsFilters({ ...filters, searched: valueSearched }));
    setInputValue(valueSearched);
  };

  const handleKeyDown = (e) => {
    if (width < 768 && e.key === 'Enter') setFilterActive(false);
  };

  const conditionClass = useMemo(() => {
    return width < 768 && removeClassOnAssetDetailsFilters && !isFilterActive
      ? style.displayNone
      : style.displayBlock;
  }, [isFilterActive]);

  const setUpdateBookmarkEvent = () => {
    const sort = !_.isEmpty(selectedSort)
      ? _.isArray
        ? { [selectedSort[0] === '# TASKS' ? 'Tasks' : selectedSort[0]]: true }
        : selectedSort
      : {};
    const filtersString = {
      filters: {
        ...filters,
        searched: inputValue
      },
      sort,
      equipmentId,
      siteId,
      structureId
    };

    const siteBookmarkData = {
      filters: JSON.stringify(filtersString),
      bookmarkId
    };

    ToasterUtil.showSuccess(`Bookmark changes saved.`);
    BookmarksService.updateBookmarkData({ bookmarkId, updatedData: siteBookmarkData });
    dispatch?.(bookmarksActions.setBookmarkValidity(true));

    BookmarksService.updateBookmarksNameAndAtAGlance({
      name: 'Equipment',
      atAGlance: true,
      bookmarkId,
      updatedData: siteBookmarkData
    });

    const params = new URLSearchParams(searchParams);
    params.set('bookmarkFilter', JSON.stringify(filtersString));
    setSearchParams(params, { replace: true });
    setUpdateBookmarkModalVisible(false);
  };

  const filterConfigs = useMemo(() => {
    const filterConfigs = dynamicFilterList({
      updateFilters,
      type: 'equipment-listing',

      filters,
      areaHierarchyDataMap,
      picklistsMap,
      taskTypeTag,
      taskStatusTags,
      inspectionTypeTagsMap,
      inspectionRunTags,
      taskRecurringTemplateTags,
      assignableUsersTags,
      checkTypeTagsMap,
      workOrderTypeTagMap
    });

    return filterConfigs;
  }, [
    filters,
    areaHierarchyDataMap,
    picklistsMap,
    taskTypeTag,
    taskStatusTags,
    inspectionTypeTagsMap,
    inspectionRunTags,
    taskRecurringTemplateTags,
    assignableUsersTags,
    checkTypeTagsMap,
    workOrderTypeTagMap
  ]);

  return (
    <>
      <div
        className={`op-sidebarfilter ${isActive && 'open'} ${!noGap && 'gap'} 
        ${conditionClass} ${isActive ? style.isActivePadding : style.isInActivePadding}
        `}
      >
        <div
          onTouchStart={startTouch}
          onTouchMove={moveTouch}
          className={`${isActive ? style.paddingLeftAndRight : style.isInActivePadding}`}
        >
          <FilterHeader
            title="Filters"
            filterCount={filtersCount || 0}
            resetBtnEvent={() => resetBtnEvent({})}
            leftBtnEvent={leftBtnEvent}
            openBookmarkModalEvent={() => {
              if (bookmarkId || isFilterChangedInBookmark) {
                if (isFilterChangedInBookmark) setUpdateBookmarkModalVisible(true);
              } else {
                setIsModalVisible(true);
              }
            }}
            bookmarkId={bookmarkId ? true : false}
            isFilterChangedInBookmark={isFilterChangedInBookmark}
            width={width}
            detailPage={detailPage}
          />
        </div>
        <div
          className={`op-filter-search ${
            isActive ? style.paddingLeftAndRight : style.isInActivePadding
          } `}
          style={{ marginTop: '25px', marginBottom: '16px' }}
        >
          <SearchField
            placeholder="Search"
            value={inputValue}
            onKeyDown={handleKeyDown}
            onChange={handleUserInput}
            isClearable
          />

          <DynamicFilterContainer
            filterConfigs={filterConfigs}
            filters={filters}
            unit={unit}
            filterCount={dynamicFilterCountOnEachField || 0}
            resetBtnEvent={() => resetBtnEvent({})}
          />
        </div>

        <EquipmentFilterGroups
          equipmentCategoryTags={equipmentCategoryTags}
          picklistsMap={picklistsMap}
          filters={filters}
          updateFilters={updateFilters}
          equipmentTypeTags={equipmentTypeTags}
          equipmentSubTypeTags={equipmentSubTypeTags}
          equipmentStatusTags={equipmentStatusTags}
        />

        {!removeClassOnAssetDetailsFilters && (
          <SiteFilterGroups
            coordinates={coordinates}
            filters={filters}
            updateFilters={updateFilters}
            isActive={isActive}
            treeData={treeData}
            areaHierarchyTreeDataMap={areaHierarchyTreeDataMap}
            siteTypeTags={siteTypeTags}
            picklistsMap={picklistsMap}
            siteStatusTags={siteStatusTags}
            unit={unit}
            equipmentListing
          />
        )}

        {!removeClassOnAssetDetailsFilters && (
          <TaskFilterGroups
            taskTypeTag={taskTypeTag}
            filters={filters}
            updateFilters={updateFilters}
            taskStatusTags={taskStatusTags}
            workOrderTypeTags={workOrderTypeTags}
            assignableUsersTags={assignableUsersTags}
            picklistsMap={picklistsMap}
            workOrderPriorityTags={workOrderPriorityTags}
            taskRecurringTemplateTags={taskRecurringTemplateTags}
            isActive={isActive}
            checkRecurrancePriorityTags={checkRecurrancePriorityTags}
            checkTypeTags={checkTypeTags}
            checkTypeTagsMap={checkTypeTagsMap}
            inspectionTypeTagsMap={inspectionTypeTagsMap}
            inspectionTypeTags={inspectionTypeTags}
            inspectionRunTags={inspectionRunTags}
            workOrderTypeTagsMap={workOrderTypeTagMap}
          />
        )}

        <ResetAndViewButton
          clickResetButton={() => resetBtnEvent({ filterActive: false })}
          length={length}
          setFilterActiveEvent={(value) => setFilterActive(value)}
          setIsModalVisibleEvent={(value) => setIsModalVisible(value)}
          width={width}
          detailPage={detailPage}
          updateBookmarkId={bookmarkId}
          bookmarkName={bookmarkName}
          setUpdateBookmarkEvent={() => {
            setUpdateBookmarkModalVisible(true);
          }}
        />
      </div>

      {isModalVisible && (
        <EquipmentBookmark
          open={isModalVisible}
          setOpen={(value) => {
            setIsModalVisible(value);
          }}
          selectedSort={selectedSort}
          filters={{ ...filters, searched: inputValue }}
          siteId={siteId}
          parentEquipmentId={equipmentId}
          structureId={structureId}
          psvs={psvs}
          pvs={pvs}
          keyName={keyNameFilter}
          tab={tabFilter}
        />
      )}

      {updateBookmarkModalVisible && (
        <SaveModal
          open={updateBookmarkModalVisible}
          setOpen={(value) => {
            setUpdateBookmarkModalVisible(value);
          }}
          bookmarkSaveChanges={true}
          onSave={setUpdateBookmarkEvent}
        />
      )}
    </>
  );
};

export default React.memo(EquipmentFilter, _.isEqual);
